import React, { useMemo, useState } from 'react'
import TimeseriesWidgets from './TimeseriesWidgets'
import TimeseriesChart from './TimeseriesChart'
import { TimeseriesStatStyle } from '../statsConf'
import { ChartOptionsType } from '../../../utils/hooks/performanceStats/useTimeseries'
import ErrorAlert from '../../error/ErrorAlert'
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Skeleton } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useIsMobileLayout } from '../../../utils/hooks/useIsMobileLayout'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import CloseIcon from '@mui/icons-material/Close'
import { Dayjs } from 'dayjs'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'

type TimeseriesProps<T extends Record<string, number>> = {
  total: T
  data: (T & { date: Dayjs })[]
  timezone: string
  chartItemsVisibility: Record<keyof T, boolean>
  statStyles: Record<keyof T, TimeseriesStatStyle>
  toggleVisibility?: (name: keyof T) => void
  dateFormat: string
  chartOptions: ChartOptionsType
  translationPrefix: string
  translateValues?: boolean
  loading: boolean
  error?: Error
}

const Timeseries = <T extends Record<string, number>>({
  total,
  data,
  timezone,
  statStyles,
  chartItemsVisibility,
  toggleVisibility,
  dateFormat,
  chartOptions,
  translationPrefix,
  translateValues = true,
  error,
  loading,
}: TimeseriesProps<T>): JSX.Element => {
  const t = useAppTranslations()
  const isMobile = useIsMobileLayout()
  const isLandscape = useMediaQuery('(orientation: landscape)')
  const [chartOpen, setChartOpen] = useState(false)

  const fullscreenChartHeight = useMemo(
    () => (isLandscape ? 'calc(100dvh - 6.4rem)' : 'calc(100dvh - 8rem)'),
    [isLandscape]
  )

  const chart = (
    <TimeseriesChart
      chartDateFormat={dateFormat}
      chartItemsVisibility={chartItemsVisibility}
      chartOptions={chartOptions}
      data={data}
      height={isMobile ? '100%' : 350}
      statStyles={statStyles}
      timezone={timezone}
      translateValues={translateValues}
      translationPrefix={translationPrefix}
    />
  )

  if (error) return <ErrorAlert message={error.message} />

  return (
    <>
      <TimeseriesWidgets
        total={total}
        statStyles={statStyles}
        chartItemsVisibility={chartItemsVisibility}
        handleToggleVisibility={toggleVisibility}
        translationPrefix={translationPrefix}
        translateValues={translateValues}
        loading={loading}
      />
      {loading ? (
        isMobile ? (
          <Skeleton height={48} sx={{ mb: 2 }} variant="rectangular" />
        ) : (
          <Skeleton height="350px" variant="rectangular" />
        )
      ) : (
        data &&
        (isMobile ? (
          <>
            <Button fullWidth onClick={() => setChartOpen(true)} startIcon={<ShowChartIcon />} sx={{ mb: 2 }}>
              {t('stats.chart.showFullscreen')}
            </Button>
            <Dialog fullScreen onClose={() => setChartOpen(false)} open={chartOpen}>
              <DialogTitle
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 1,
                }}
              >
                {t('stats.chart.title')}
                <IconButton aria-label={t('stats.chart.close')} onClick={() => setChartOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent
                sx={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  minHeight: 0,
                  p: 1,
                }}
              >
                <Box sx={{ height: fullscreenChartHeight, minHeight: 0, width: '100%' }}>{chart}</Box>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          chart
        ))
      )}
    </>
  )
}

export default Timeseries
